import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import "./createTickets.css";

function CustomDropdown({ id, options, value, onChange, disabled, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  return (
    <div className={`agce-custom-dropdown ${disabled ? "disabled" : ""}`} ref={containerRef}>
      <div
        id={id}
        className={`agce-custom-dropdown__header ${isOpen ? "open" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={selectedOption ? "" : "placeholder"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <i className={`ri-arrow-down-s-line agce-custom-dropdown__arrow ${isOpen ? "open" : ""}`} aria-hidden="true" />
      </div>
      {isOpen && !disabled && (
        <ul className="agce-custom-dropdown__list">
          {options.map((opt) => (
            <li
              key={opt.value}
              className={`agce-custom-dropdown__item ${String(opt.value) === String(value) ? "selected" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
              {String(opt.value) === String(value) && <i className="ri-check-line check-icon" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function extractCategoriesPriorities(res) {
  const d = res?.data;
  if (!d || typeof d !== "object") return { categories: [], priorities: [] };
  const inner = d.data && typeof d.data === "object" && !Array.isArray(d.data) ? d.data : d;
  const categories = Array.isArray(inner.categories) ? inner.categories : [];
  const priorities = Array.isArray(inner.priorities) ? inner.priorities : [];
  return { categories, priorities };
}

const DEFAULT_PRIORITIES = [
  { id: "low", name: "Low" },
  { id: "medium", name: "Medium" },
  { id: "high", name: "High" },
];

const DESCRIPTION_MAX = 2000;
const ALLOWED_FILE_TYPES = ".png,.jpg,.jpeg,.pdf";
const ALLOWED_FILE_LABEL = "PNG, JPG, JPEG, PDF";
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export default function CreateTicketsPage() {
  const navigate = useNavigate();
  const issueImageRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [categoriesList, setCategoriesList] = useState([]);
  const [prioritiesList, setPrioritiesList] = useState(DEFAULT_PRIORITIES);
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (DISABLE_ALL_NETWORK) {
      setPrioritiesList(DEFAULT_PRIORITIES);
      setCategoriesList([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getTicketCategories();
        if (cancelled) return;
        if (res?.success === false && res?.message) alertErrorMessage(res.message);
        const { categories, priorities } = extractCategoriesPriorities(res);
        if (priorities.length) setPrioritiesList(priorities);
        else setPrioritiesList(DEFAULT_PRIORITIES);
        setCategoriesList(categories);
      } catch {
        if (!cancelled) alertErrorMessage("Could not load ticket categories.");
        setPrioritiesList(DEFAULT_PRIORITIES);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!prioritiesList.length) return;
    setPriority((prev) => {
      if (prioritiesList.some((p) => String(p?.id) === prev)) return prev;
      const med = prioritiesList.find((p) => String(p?.id).toLowerCase() === "medium");
      return med ? String(med.id) : String(prioritiesList[0].id);
    });
  }, [prioritiesList]);

  const processFile = useCallback((file) => {
    if (!file) return;
    const ext = file.name?.split(".").pop()?.toLowerCase() || "";
    const allowed = ["png", "jpg", "jpeg", "pdf"];
    if (!allowed.includes(ext)) {
      alertErrorMessage(`Only ${ALLOWED_FILE_LABEL} files are allowed.`);
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      alertErrorMessage(`File must be under ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    // Put the file into the hidden input via DataTransfer
    const dt = new DataTransfer();
    dt.items.add(file);
    if (issueImageRef.current) {
      issueImageRef.current.files = dt.files;
    }
    setFileName(file.name);
    const sizeMB = file.size / (1024 * 1024);
    setFileSize(sizeMB >= 1 ? `${sizeMB.toFixed(2)} MB` : `${(file.size / 1024).toFixed(1)} KB`);
  }, []);

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleRemoveFile = useCallback(() => {
    if (issueImageRef.current) issueImageRef.current.value = "";
    setFileName("");
    setFileSize("");
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const subj = subject.trim();
      const desc = description.trim();
      if (!subj) {
        alertErrorMessage("Subject is required");
        return;
      }
      if (!desc) {
        alertErrorMessage("Description is required");
        return;
      }
      if (!category || !String(category).trim()) {
        alertErrorMessage("Category is required");
        return;
      }

      const pri = (priority || "medium").trim().toLowerCase() || "medium";

      if (DISABLE_ALL_NETWORK) {
        alertSuccessMessage("Ticket submitted (offline demo).");
        return;
      }

      const fd = new FormData();
      fd.append("subject", subj);
      fd.append("description", desc);
      fd.append("category", String(category).trim());
      fd.append("priority", pri);
      const file = issueImageRef.current?.files?.[0];
      if (file) fd.append("issue_image", file, file.name);

      setSubmitting(true);
      LoaderHelper.loaderStatus(true);
      try {
        const res = await AuthService.submitTicket(fd);
        const ok = res?.success === true || res?.success === 1;
        if (ok) {
          alertSuccessMessage(res?.message || "Ticket submitted successfully.");
          setSubject("");
          setDescription("");
          setCategory("");
          handleRemoveFile();
          const med = prioritiesList.find((p) => String(p?.id).toLowerCase() === "medium");
          setPriority(med ? String(med.id) : prioritiesList[0]?.id ? String(prioritiesList[0].id) : "medium");
          navigate("/user_profile/my_tickets");
          return;
        }
        alertErrorMessage(res?.message || "Could not submit ticket.");
      } finally {
        LoaderHelper.loaderStatus(false);
        setSubmitting(false);
      }
    },
    [subject, description, category, priority, prioritiesList, navigate, handleRemoveFile],
  );

  const descriptionCount = description.length;

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section Overview_mid">
        <div className="kyc_approval_s agce-mtickets-wrap">
          <div className="cnt agce-ctickets">

            {/* ─── Header ─── */}
            <div className="agce-ctickets__header">
              <div className="agce-ctickets__header-left">
                <nav className="agce-ctickets__breadcrumb" aria-label="Breadcrumb">
                  <Link to="/user_profile/my_tickets" className="agce-ctickets__breadcrumb-link">
                    <i className="ri-arrow-left-s-line" aria-hidden="true" />
                    My Tickets
                  </Link>
                  <span className="agce-ctickets__breadcrumb-sep">/</span>
                  <span className="agce-ctickets__breadcrumb-current">Create New</span>
                </nav>
                <h1 className="agce-ctickets__title">Create a Support Ticket</h1>
                <p className="agce-ctickets__sub">
                  Describe your issue and our team will get back to you within 24 hours.
                </p>
              </div>
              <div className="agce-ctickets__header-icon" aria-hidden="true">
                <i className="ri-customer-service-2-line" />
              </div>
            </div>

            {/* ─── Form ─── */}
            <form className="agce-ctickets__form" onSubmit={handleSubmit} autoComplete="off">

              {/* Row 1: Subject + Category */}
              <div className="agce-ctickets__row">
                <div className="agce-ctickets__field">
                  <label className="agce-ctickets__label" htmlFor="ct-subject">
                    <i className="ri-edit-line agce-ctickets__label-icon" aria-hidden="true" />
                    Subject <span className="agce-ctickets__required">*</span>
                  </label>
                  <input
                    id="ct-subject"
                    className="agce-ctickets__input"
                    type="text"
                    placeholder="Brief summary of your issue"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    disabled={submitting}
                  />
                </div>

                <div className="agce-ctickets__field">
                  <label className="agce-ctickets__label" htmlFor="ct-category">
                    <i className="ri-folder-line agce-ctickets__label-icon" aria-hidden="true" />
                    Category <span className="agce-ctickets__required">*</span>
                  </label>
                  <div className="agce-ctickets__select-wrap">
                    <CustomDropdown
                      id="ct-category"
                      value={category}
                      onChange={setCategory}
                      disabled={submitting}
                      placeholder="Select a category"
                      options={categoriesList.map((c) => ({
                        value: String(c?.id ?? ""),
                        label: c?.name != null ? String(c.name) : String(c?.id ?? "")
                      }))}
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Priority + File Upload */}
              <div className="agce-ctickets__row">
                <div className="agce-ctickets__field">
                  <label className="agce-ctickets__label" htmlFor="ct-priority">
                    <i className="ri-flag-line agce-ctickets__label-icon" aria-hidden="true" />
                    Priority
                  </label>
                  <div className="agce-ctickets__select-wrap">
                    <CustomDropdown
                      id="ct-priority"
                      value={priority}
                      onChange={setPriority}
                      disabled={submitting}
                      placeholder="Select a priority"
                      options={prioritiesList.map((p) => ({
                        value: String(p?.id ?? ""),
                        label: p?.name != null ? String(p.name) : String(p?.id ?? "")
                      }))}
                    />
                  </div>
                </div>

                <div className="agce-ctickets__field">
                  <label className="agce-ctickets__label">
                    <i className="ri-attachment-2 agce-ctickets__label-icon" aria-hidden="true" />
                    Attachment <span className="agce-ctickets__optional">(optional)</span>
                  </label>
                  <input
                    ref={issueImageRef}
                    type="file"
                    accept={ALLOWED_FILE_TYPES}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    disabled={submitting}
                  />
                  {fileName ? (
                    <div className="agce-ctickets__file-attached">
                      <div className="agce-ctickets__file-info">
                        <i className="ri-file-line agce-ctickets__file-icon" aria-hidden="true" />
                        <div className="agce-ctickets__file-meta">
                          <span className="agce-ctickets__file-name" title={fileName}>{fileName}</span>
                          <span className="agce-ctickets__file-size">{fileSize}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="agce-ctickets__file-remove"
                        onClick={handleRemoveFile}
                        disabled={submitting}
                        aria-label="Remove file"
                      >
                        <i className="ri-close-circle-fill" aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <div
                      ref={dropZoneRef}
                      className={`agce-ctickets__dropzone${dragActive ? " agce-ctickets__dropzone--active" : ""}`}
                      onClick={() => issueImageRef.current?.click()}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          issueImageRef.current?.click();
                        }
                      }}
                    >
                      <i className="ri-upload-cloud-2-line agce-ctickets__dropzone-icon" aria-hidden="true" />
                      <span className="agce-ctickets__dropzone-text">
                        <strong>Click to upload</strong> or drag & drop
                      </span>
                      <span className="agce-ctickets__dropzone-hint">
                        {ALLOWED_FILE_LABEL} · Max {MAX_FILE_SIZE_MB} MB
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 3: Description (full width) */}
              <div className="agce-ctickets__field agce-ctickets__field--full">
                <label className="agce-ctickets__label" htmlFor="ct-description">
                  <i className="ri-file-text-line agce-ctickets__label-icon" aria-hidden="true" />
                  Description <span className="agce-ctickets__required">*</span>
                </label>
                <div className="agce-ctickets__textarea-wrap">
                  <textarea
                    id="ct-description"
                    className="agce-ctickets__textarea"
                    placeholder="Please provide as much detail as possible about your issue..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={DESCRIPTION_MAX}
                    disabled={submitting}
                  />
                  <span
                    className={`agce-ctickets__char-count${descriptionCount > DESCRIPTION_MAX * 0.9 ? " agce-ctickets__char-count--warn" : ""}`}
                  >
                    {descriptionCount}/{DESCRIPTION_MAX}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="agce-ctickets__actions">
                <button
                  type="button"
                  className="agce-ctickets__btn agce-ctickets__btn--cancel"
                  onClick={() => navigate("/user_profile/my_tickets")}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="agce-ctickets__btn agce-ctickets__btn--submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line agce-ctickets__spin" aria-hidden="true" /> Submitting…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" aria-hidden="true" /> Submit Ticket
                    </>
                  )}
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
}
